import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Heart, ShoppingCart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCustomer } from "@/context/CustomerContext";
import { useCart } from "@/context/CartContext";

const priceLabel = (n) => "$ " + (Math.round(Number(n) || 0)).toLocaleString("es-CO");

export default function FavoritesDialog() {
  const { favOpen, setFavOpen, favorites, toggleFavorite } = useCustomer();
  const { addItem } = useCart();
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (favOpen) {
      setLoading(true);
      api.get("/customer/favorites")
        .then((r) => setBooks(r.data))
        .catch(() => setBooks([]))
        .finally(() => setLoading(false));
    }
  }, [favOpen, favorites.length]);

  return (
    <Dialog open={favOpen} onOpenChange={setFavOpen}>
      <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="favorites-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-extrabold text-2xl text-[var(--lc-ink)] flex items-center gap-2">
            <Heart className="w-6 h-6 text-[var(--lc-primary)] fill-[var(--lc-primary)]" />
            Mis favoritos
          </DialogTitle>
          <DialogDescription className="text-[var(--lc-slate)]">
            Los libros que guardaste para más tarde.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[var(--lc-primary)]" /></div>
        ) : books.length === 0 ? (
          <div className="py-12 text-center text-[var(--lc-slate)]">
            <Heart className="w-12 h-12 mx-auto mb-3 opacity-40" />
            Aún no tienes libros favoritos.
          </div>
        ) : (
          <div className="space-y-3">
            {books.map((b) => (
              <div key={b.id} className="flex gap-3 bg-[var(--lc-bg)] rounded-2xl p-3" data-testid={`favorite-item-${b.id}`}>
                <img src={b.image_url} alt={b.title} className="w-14 h-20 object-cover object-top rounded-lg shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-[var(--lc-ink)] leading-tight line-clamp-1">{b.title}</p>
                  <p className="text-sm text-[var(--lc-slate)] line-clamp-1">{b.author}</p>
                  <p className="font-display font-bold text-[var(--lc-ink)] mt-0.5">{priceLabel(b.price)}</p>
                  <div className="mt-1.5 flex gap-2">
                    <button
                      onClick={() => { addItem(b); toast.success(`"${b.title}" agregado al carrito`); }}
                      data-testid={`favorite-add-${b.id}`}
                      className="flex items-center gap-1 rounded-full bg-[var(--lc-primary)] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[var(--lc-primary-hover)]"
                    >
                      <ShoppingCart className="w-3.5 h-3.5" /> Agregar
                    </button>
                    <button
                      onClick={() => toggleFavorite(b)}
                      data-testid={`favorite-remove-${b.id}`}
                      className="flex items-center gap-1 rounded-full border border-[var(--lc-border)] text-[var(--lc-slate)] px-3 py-1.5 text-xs font-semibold hover:bg-white"
                    >
                      <Heart className="w-3.5 h-3.5 fill-[var(--lc-primary)] text-[var(--lc-primary)]" /> Quitar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
